import { useEffect, useMemo, useState } from "react";
import {
    Box, Button, Chip, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Typography, IconButton, Tooltip
} from "@mui/material";
import Templete from "../../template/Template";
import SimpleBackdrop from "../../components/Loading/SimpleBackdrop";
import { AlertInfo } from "../../components/Alerts/Alert";
import { api } from "../../services/api";
import { formatDate } from "../../utilities/date";
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import PersonIcon from '@mui/icons-material/Person';
import GavelIcon from '@mui/icons-material/Gavel';

type ZapsignDoc = {
    id_zapsign_doc: number
    nome_documento: string
    status_zapsign: string
    sign_url_cliente: string | null
    sign_url_advogado: string | null
    signed_file_url: string | null
    create_zapsign_doc: string | null
    nome_cliente: string | null
    protocolo_processo: string | null
    causa_processual: string | null
}

type FiltroStatus = 'todos' | 'pending' | 'signed' | 'refused';

const statusInfo: Record<string, { label: string; color: 'warning' | 'success' | 'error' | 'default' }> = {
    pending: { label: 'Pendente', color: 'warning' },
    signed: { label: 'Assinado', color: 'success' },
    refused: { label: 'Recusado', color: 'error' },
};

function getStatusInfo(status: string) {
    return statusInfo[status] || { label: status || '—', color: 'default' as const };
}

export default function Assinaturas() {
    const [openLoad, setOpenLoad] = useState(false);
    const [docs, setDocs] = useState<ZapsignDoc[]>([]);
    const [filtro, setFiltro] = useState<FiltroStatus>('todos');

    async function loadData() {
        setOpenLoad(true);
        try {
            const response = await api.get('/listardocumentoszapsign');
            if (Array.isArray(response.data)) {
                setDocs(response.data);
            } else if (response.data?.error) {
                AlertInfo({ icon: 'error', title: response.data.msg || 'Falha ao carregar documentos' });
            }
        } catch (error) {
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setOpenLoad(false);
    }

    useEffect(() => {
        loadData();
    }, []);

    const docsFiltrados = useMemo(() => {
        if (filtro === 'todos') return docs;
        return docs.filter(d => d.status_zapsign === filtro);
    }, [docs, filtro]);

    const contagem = useMemo(() => ({
        todos: docs.length,
        pending: docs.filter(d => d.status_zapsign === 'pending').length,
        signed: docs.filter(d => d.status_zapsign === 'signed').length,
        refused: docs.filter(d => d.status_zapsign === 'refused').length,
    }), [docs]);

    function copiarLink(url: string) {
        navigator.clipboard.writeText(url)
            .then(() => AlertInfo({ icon: 'success', title: 'Link copiado!' }))
            .catch(() => AlertInfo({ icon: 'error', title: 'Não foi possível copiar' }));
    }

    function renderLink(url: string | null, label: string, icon: JSX.Element) {
        if (!url) return null;
        return (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.3 }}>
                {icon}
                <Typography variant="caption" sx={{ minWidth: 56 }}>{label}</Typography>
                <Tooltip title="Copiar link">
                    <IconButton size="small" color="primary" onClick={() => copiarLink(url)}>
                        <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Abrir link">
                    <IconButton size="small" onClick={() => window.open(url, '_blank')}>
                        <OpenInNewIcon fontSize="inherit" />
                    </IconButton>
                </Tooltip>
            </Stack>
        );
    }

    return (
        <Templete menuActive="assinaturas">
            <SimpleBackdrop open={openLoad} />

            <Box sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <DriveFileRenameOutlineIcon color="primary" />
                    <Typography variant="h5" fontWeight="bold">Assinaturas (ZapSign)</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    Acompanhe os documentos enviados para assinatura eletrônica e seus status
                </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
                <Chip
                    label={`Todos (${contagem.todos})`}
                    color={filtro === 'todos' ? 'primary' : 'default'}
                    variant={filtro === 'todos' ? 'filled' : 'outlined'}
                    onClick={() => setFiltro('todos')}
                />
                <Chip
                    label={`Pendentes (${contagem.pending})`}
                    color={filtro === 'pending' ? 'warning' : 'default'}
                    variant={filtro === 'pending' ? 'filled' : 'outlined'}
                    onClick={() => setFiltro('pending')}
                />
                <Chip
                    label={`Assinados (${contagem.signed})`}
                    color={filtro === 'signed' ? 'success' : 'default'}
                    variant={filtro === 'signed' ? 'filled' : 'outlined'}
                    onClick={() => setFiltro('signed')}
                />
                <Chip
                    label={`Recusados (${contagem.refused})`}
                    color={filtro === 'refused' ? 'error' : 'default'}
                    variant={filtro === 'refused' ? 'filled' : 'outlined'}
                    onClick={() => setFiltro('refused')}
                />
                <Box sx={{ flex: 1 }} />
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}>
                    Atualizar
                </Button>
            </Stack>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell><strong>Documento</strong></TableCell>
                            <TableCell><strong>Cliente</strong></TableCell>
                            <TableCell><strong>Processo</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Enviado em</strong></TableCell>
                            <TableCell><strong>Links de assinatura</strong></TableCell>
                            <TableCell><strong>Assinado</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {docsFiltrados.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Nenhum documento enviado para assinatura.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            docsFiltrados.map(d => {
                                const si = getStatusInfo(d.status_zapsign);
                                return (
                                    <TableRow key={d.id_zapsign_doc} hover>
                                        <TableCell>{d.nome_documento || '—'}</TableCell>
                                        <TableCell>{d.nome_cliente || '—'}</TableCell>
                                        <TableCell>
                                            {d.protocolo_processo || d.causa_processual
                                                ? (
                                                    <Box>
                                                        {d.protocolo_processo && (
                                                            <Typography variant="body2">{d.protocolo_processo}</Typography>
                                                        )}
                                                        {d.causa_processual && (
                                                            <Typography variant="caption" color="text.secondary">{d.causa_processual}</Typography>
                                                        )}
                                                    </Box>
                                                )
                                                : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={si.label} color={si.color} />
                                        </TableCell>
                                        <TableCell>
                                            {d.create_zapsign_doc ? formatDate(d.create_zapsign_doc, "br") : '—'}
                                        </TableCell>
                                        <TableCell>
                                            {renderLink(d.sign_url_cliente, 'Cliente', <PersonIcon fontSize="inherit" color="action" />)}
                                            {renderLink(d.sign_url_advogado, 'Advogado', <GavelIcon fontSize="inherit" color="action" />)}
                                            {!d.sign_url_cliente && !d.sign_url_advogado && (
                                                <Typography variant="caption" color="text.secondary">—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {d.signed_file_url ? (
                                                <Tooltip title="Abrir documento assinado">
                                                    <IconButton size="small" color="success" onClick={() => window.open(d.signed_file_url as string, '_blank')}>
                                                        <DownloadIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">—</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Templete>
    );
}
